import { useRoute, useRouter } from 'vue-router';
import { ElectronApi } from '@/services/electron.api';
import { Description, Introduce } from '@/types/watchface';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { emitter, EventType } from '@/utils/emitter.utils';
import { pickWatchfacePath } from '@/utils/fs/pick-file.utils';
import { emptyDir, ensureDir, testAccess } from '@/utils/fs/fs-extra.utils';
import { WatchfaceService } from '@/services/watchface';
import { Modal, notification } from 'ant-design-vue';
import { uniqueFileName } from '@/utils/fs/name.utils';
import { useWatchfaceHistory } from './use-watchface-history';
import { toEditorConfigJson } from '@/utils/manifestToJson';
import { xmlToAst } from '@/utils/xmlTool';
import { getWatchFaceType } from '@/utils/manifestToJson/utils';
import { useHomePreviewImgs } from '.';
import { validWatchfaceName } from '@/utils/watchface-valid.utils';
import {
  getWatchfaceDefaultId,
  getWatchfacePrivateId,
  getWatchfaceRandomId
} from '@/utils/watchface-id.utils';
import { pickZipExtensions } from '@/utils/fs/zip.utils';
import { canUse } from '@/utils/can-use.utils';
import { useTranslationStore } from '@/store/use-translation-store';

export type WatchfaceFormData = Description &
  Introduce & {
    dir: string;
    shape: 'circle' | 'square' | 'run';
  };

export const useWatchfaceManage = () => {
  const router = useRouter();
  const route = useRoute();
  const watchfaceStore = useWatchfaceStore();
  const translationStore = useTranslationStore();
  const { replacePath: replaceHistoryPath, deleteFromStorage } =
    useWatchfaceHistory();

  const { clear: clearPreviewImg } = useHomePreviewImgs();

  /**
   * 打开新的表盘文件，并进入editor页面
   */
  const gotoWachfaceEditor = async (path: string) => {
    try {
      // console.clear();
      const editor = 'editor';
      router
        .push({
          name: editor,
          query: { path, _key: Math.random() }
        })
        .then(() => {
          // 这样可以确保返回首页后，重新生成预览图
          clearPreviewImg(path);
        });
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * 选择并打开表盘
   * @returns
   */
  const pickThenOpen = async () => {
    try {
      const { path } = ElectronApi;
      if (!path) return;
      //选择路径
      const { path: watchfaceDir } =
        (await pickWatchfacePath('all', pickZipExtensions)) ?? {};
      if (!watchfaceDir) return;

      const editorConfig = path.join(watchfaceDir, 'editor.config.json');
      const hasConfig = await testAccess(editorConfig);
      if (!hasConfig) {
        await openLowerVersionWatchface(watchfaceDir);
      } else {
        await gotoWachfaceEditor(watchfaceDir);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const generatePkgName = (deviceType: string) => {
    const canUseAutoId = canUse('auto-watchface-id', deviceType).valid;
    return canUseAutoId ? getWatchfaceDefaultId() : getWatchfaceRandomId();
  };

  const _resetPkgName = (description: Description) => {
    description.pkgName = generatePkgName(description.deviceType ?? '');
    return description;
  };

  /**
   * 复制表盘,返回新表盘路径
   */
  const cloneWatchface = async (fromPath: string) => {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path || !fromPath) return;

    // 表盘保存目录
    const dir = path.dirname(fromPath);
    const description = await new WatchfaceService(
      fromPath
    ).description.getDescriptionData();
    if (!description) return;
    const name = await uniqueFileName(dir, description.name + '_副本');
    if (!name) return;
    description.name = name;

    // 重置id值
    _resetPkgName(description);

    // 新表盘目录
    const to = path.join(dir, name);
    // 复制
    await fs.promises.cp(fromPath, to, { recursive: true });
    // 替换description
    await new WatchfaceService(to).description.writeDecsriptionXML(description);

    return to;
  };

  /**
   * 是否覆盖已有表盘
   * @param watchfacePath
   * @returns {Promise<boolean>} true:已覆盖或不存在; false：用户不同意覆盖
   */
  const overwriteIfExists = (watchfacePath: string) => {
    const fs = ElectronApi.fs;
    return new Promise<boolean>((resolve, rejcet) => {
      testAccess(watchfacePath)
        .then((exists) => {
          if (!exists) {
            resolve(true);
            return;
          }

          Modal.confirm({
            title: '提示',
            content: '表盘名称已存在，是否覆盖已有表盘？',
            okText: '确认',
            cancelText: '取消',
            zIndex: 1002,
            onOk: () => {
              // 删除已存在的
              fs?.promises
                .rm(watchfacePath, { recursive: true })
                .then(() => {
                  // 从history中删除
                  deleteFromStorage(watchfacePath);
                  resolve(true);
                })
                .catch((err) => rejcet(err));
            },
            onCancel() {
              resolve(false);
            }
          });
        })
        .catch((err) => rejcet(err));
    });
  };

  const _generateWatchfacePath = (
    data: WatchfaceFormData,
    watchfaceDirOld: string
  ) => {
    const path = ElectronApi.path;
    const { dir = '', name = '', deviceType, pkgName } = data;
    if (!dir || !name || !path) return;

    // 当未启用默认id或已启动，单当前id不是默认id时，需要增加前缀
    let needPrefix =
      !canUse('auto-watchface-id', data.deviceType).valid ||
      pkgName != getWatchfaceDefaultId();

    // 如果是M66
    // 如果自动生成id，则去掉前缀（前缀已没有意义）
    if (deviceType == 'M66') {
      needPrefix = false;
    }

    const watchfaceDirName =
      needPrefix && pkgName ? pkgName + '_' + name : name;
    const watchfacePathNew = path.join(dir, watchfaceDirName);
    return {
      watchfacePath: watchfacePathNew,
      name,
      pkgName: pkgName,
      changed: !watchfaceDirOld.startsWith(watchfacePathNew)
    };
  };

  /**
   * 新建表盘，新建成功后，自动跳转到editor页面
   * @param formData
   * @returns
   */
  const createWatchface = async (formData: WatchfaceFormData) => {
    const watchfacePath = _generateWatchfacePath(formData, '')?.watchfacePath;
    if (!watchfacePath) throw 'watchfacePath未提供';

    const canOverwrite = await overwriteIfExists(watchfacePath);
    // 如果不同意覆盖
    if (!canOverwrite) {
      // do nothong
      return;
    }
    await WatchfaceService.create(watchfacePath, {
      ...formData,
      _id: getWatchfacePrivateId(formData.deviceType ?? watchfacePath),
      watchOS: formData.watchOS ?? 'vela'
    } as Description);
    return watchfacePath;
  };

  /**
   * 修改表盘信息
   * @param watchfacePath 要编辑的表盘路径
   * @param formData
   * @returns true：操作成功，否则false
   */
  const editWatchface = async (
    watchfacePath: string,
    formData: WatchfaceFormData,
    introData: Introduce
  ) => {
    const path = ElectronApi.path;
    const fs = ElectronApi.fs;
    if (!fs || !path) return;

    const valid = validWatchfaceName(formData.name ?? '', formData.deviceType);
    if (typeof valid == 'string') return;

    const isEditorPage = route.name == 'editor';

    const { watchfacePath: watchfacePathNewTemp, changed = false } =
      _generateWatchfacePath(formData, watchfacePath) ?? {};
    let watchfacePathNew =
      (changed ? watchfacePathNewTemp : watchfacePath) ?? watchfacePath;

    // 最后处理表盘移动问题
    // 如果用户修改了保存路径，需要判断目标路径是否存在，且是否可覆盖
    if (watchfacePathNew != watchfacePath) {
      const canOverwrite = await overwriteIfExists(watchfacePathNew);

      // 如果不同意覆盖
      if (!canOverwrite) {
        // do nothong
        return;
      }
      // 此时，目标路径应该不存在同名的了，可以
      await ensureDir(watchfacePathNew);
      try {
        await fs?.promises.rename(watchfacePath, watchfacePathNew);
      } catch (error) {
        await emptyDir(watchfacePathNew);
        await fs?.promises.cp(watchfacePath, watchfacePathNew, {
          recursive: true,
          force: true
        });
        await fs.promises.rm(watchfacePath, { recursive: true, force: true });
      }
      // 在history中，替换watchfacePath，
      replaceHistoryPath(watchfacePath, watchfacePathNew);
      if (isEditorPage) {
        watchfaceStore.path = watchfacePathNew;
      }
    } else {
      watchfacePathNew = watchfacePath;
    }

    // 如果需要移动，需要移动后再保存数据
    const service = new WatchfaceService(watchfacePathNew);
    const desc = watchfaceStore.description;
    const data = await service.description.writeDecsriptionXML({
      ...Object.assign(desc, formData)
    } as Description);
    // 写入表盘简介json数据
    service.editorConfig.writeEditorData({ introData });

    // 如果在编辑页面，需要处理path切换逻辑
    if (isEditorPage) {
      await watchfaceStore.sync(data);
    }

    if (translationStore.locales.length > 1 && data) {
      const defaultLanguage = translationStore.locales[0];
      translationStore.setTranslation(
        'watchfaceName',
        defaultLanguage,
        data.name
      );
    }

    emitter.emit(EventType['watchface:description-saved'], data);

    return true;
  };

  /**
   * 打开1.5表盘文件
   */
  const openLowerVersionWatchface = async (path: string) => {
    const wfService = new WatchfaceService(path);
    const _open = async () => {
      const manifest = await wfService.manifest.readManifestXML();
      if (!manifest) return;
      try {
        // 复制一份
        await wfService.manifest.copyManifestXML('manifest1_5.xml');
        const manifestAst = xmlToAst(manifest);
        const configJson = toEditorConfigJson(manifestAst);
        await wfService.editorConfig.writeEditorData(configJson);
        const description = await wfService.description.getDescriptionData();
        if (!description) return;
        description.watchfaceType = getWatchFaceType(manifestAst);
        description['isFrom1.5'] = true;
        await wfService.description.writeDecsriptionXML(description, {
          mode: 'create'
        });
        await gotoWachfaceEditor(path);
      } catch (e) {
        console.log(e);
        notification.warn({
          message: '打开1.5表盘失败：' + e
        });
      }
    };
    await _open();
    // Modal.confirm({
    //   title: '提示',
    //   content:
    //     '该表盘为1.5版本，是否升级为2.0版本，升级后可能会有素材丢失，是否继续升级',
    //   okText: '确认',
    //   cancelText: '取消',
    //   onOk: async () => {
    //     _open();
    //   },
    //   onCancel: () => {
    //     return;
    //   }
    // });
  };

  return {
    pickThenOpen,
    gotoWachfaceEditor,
    openLowerVersionWatchface,
    cloneWatchface,
    createWatchface,
    editWatchface,
    generatePkgName
  };
};
